import { useQuery } from '@tanstack/react-query';
import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getDashboardOpenings } from '@/api-clients/get-dashboard-openings';
import { Pagination } from '@/components/base/pagination';
import { Search } from '@/components/base/search';
import { Filters } from '@/components/pages/dashboard/filters';
import { DataTable } from '@/components/ui/data-table';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { TableProvider, useTableContext } from '@/contexts/table-context';
import { useDataTable } from '@/hooks/use-data-table';
import { queryKeys } from '@/query-keys';

import { SelectOpeningRow, useSelectOpeningsColumns } from './columns';
import { NoOpenings } from './no-openings';
import { useSelectOpeningsSelection } from './use-select-openings-selection';

const ROWS_PER_PAGE_STORAGE_KEY =
  'source.jobPosting.selectOpenings.rowsPerPage';

function SelectOpeningsContent() {
  const intl = useIntl();
  const {
    pagination: { pageIndex, pageSize },
    setRowCount,
    searchQuery,
    setSearchQuery,
    filters: { positionIds, locationIds, brandIds },
    sorting,
  } = useTableContext();
  const sort = sorting[0];

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      ...queryKeys.jobPosting.selectOpenings,
      pageIndex,
      pageSize,
      searchQuery,
      positionIds,
      locationIds,
      brandIds,
      sorting,
    ],
    queryFn: () =>
      getDashboardOpenings({
        page: pageIndex + 1,
        perPage: pageSize,
        query: searchQuery,
        positionIds,
        locationIds,
        brandIds,
        showAggregateData: true,
        sortBy: sort?.id === 'title' ? sort.id : undefined,
        sortDirection: sort?.desc ? 'desc' : 'asc',
      }),
  });

  if (isError) {
    throw new Error('Error fetching openings for job posting');
  }

  const tableRows = useMemo(
    () => (data?.data ?? []) as SelectOpeningRow[],
    [data],
  );
  const defaultMetricsPeriod = useMemo((): number | null | undefined => {
    if (data === undefined) return undefined;
    return data.default_metrics_period ?? null;
  }, [data]);

  const columns = useSelectOpeningsColumns(defaultMetricsPeriod);
  const rowCount = data?.pagination?.total_count ?? 0;

  useEffect(() => {
    setRowCount(rowCount);
  }, [rowCount, setRowCount]);

  const { table } = useDataTable<SelectOpeningRow>({
    columns,
    data: tableRows,
    rowCount,
    externalIdKey: 'external_id',
  });

  useSelectOpeningsSelection(tableRows);

  const [searchResetKey, setSearchResetKey] = useState(0);
  const handleFiltersCleared = useCallback(() => {
    setSearchResetKey(key => key + 1);
  }, []);

  const hasActiveFilters = Boolean(
    searchQuery ||
    locationIds.length > 0 ||
    positionIds.length > 0 ||
    brandIds.length > 0,
  );

  return (
    <div className="flex flex-col gap-3">
      <Typography variant="h5">
        <FormattedMessage
          defaultMessage="Select openings you would like to post"
          description="Select-openings step heading: pick openings for this posting"
        />
      </Typography>
      <div className="flex justify-between gap-2">
        <Search
          key={searchResetKey}
          initialValue={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder={intl.formatMessage({
            defaultMessage: 'Search by opening name...',
            description: 'Placeholder for the search input',
          })}
        />
        <Filters
          selectedForSourcingOnly={false}
          onCleared={handleFiltersCleared}
        />
      </div>
      <div
        data-testid="select-openings-table"
        className="border-fx-neutral-200 overflow-hidden rounded-lg border bg-white"
      >
        <DataTable
          table={table}
          columns={columns}
          isLoading={isLoading}
          emptyState={
            <NoOpenings
              hasActiveFilters={hasActiveFilters}
              onCleared={handleFiltersCleared}
            />
          }
          applyColumnSizes
        />
      </div>
      {tableRows.length > 0 && <Pagination table={table} />}
    </div>
  );
}

export function SelectOpenings() {
  const { selectedOpeningIds, setSelectedOpeningIds } = useJobPostingContext();
  const rowSelection = useMemo(
    () => Object.fromEntries(selectedOpeningIds.map(id => [id, true] as const)),
    [selectedOpeningIds],
  );
  const setRowSelection = useCallback<OnChangeFn<RowSelectionState>>(
    updater => {
      setSelectedOpeningIds(previousIds => {
        const previous = Object.fromEntries(previousIds.map(id => [id, true]));
        const next =
          typeof updater === 'function' ? updater(previous) : updater;
        return Object.keys(next).filter(id => next[id]);
      });
    },
    [setSelectedOpeningIds],
  );

  return (
    <TableProvider
      pageSizeStorageKey={ROWS_PER_PAGE_STORAGE_KEY}
      selection={{ rowSelection, setRowSelection }}
    >
      <SelectOpeningsContent />
    </TableProvider>
  );
}
